'use strict';

/**
 * The Sample workspace's Location tab.
 *
 * Display: the location the sample is in (its newest SampleLocationJoin), with its photo thumbnail, type, QR code,
 * description, the locations it sits inside, and since when the sample has been there. Edit swaps that for the Scan
 * Samples cart with this sample already in it: pick or scan the new location and Move, which goes back to the
 * display (as Cancel does). The page has its own cart (SampleLocationCart, through SampleLocationCart-Session.js),
 * separate from the Location page's and the panels' Scan Samples, with nothing saved between opens.
 *
 * Owned by SampleWorkspace-Provider.js, which forwards renderLocation / editLocation / cancelLocationEdit to it.
 */

const libSampleScanning = require('headlight-sample-scanning');
const libSampleLocationCartSession = require('./SampleLocationCart-Session.js');

// The Sample page's own cart, separate from the Location page's and the panels'.
const CART_NAME = 'SampleLocationCart';
const CART_HOST_ID = 'SampleWorkspace-LocationCart';
const BODY_SELECTOR = '#SampleWorkspace-Body';

/**
 * @typedef {Object} SampleLocationState
 * @property {boolean} Loaded
 * @property {boolean} Loading
 * @property {string} Error
 * @property {Record<string, any>|null} Location - where the sample is now
 * @property {Array<Record<string, any>>} Ancestors - the locations that one sits inside, outermost first
 * @property {Record<string, any>|null} Join - the SampleLocationJoin that puts it there
 * @property {Array<Record<string, any>>} AlsoIn - any other locations the sample is joined to
 */

class SampleWorkspaceLocationTab
{
	/**
	 * @param {any} pWorkspace - the SampleWorkspace provider
	 */
	constructor(pWorkspace)
	{
		this.workspace = pWorkspace;
		this.editing = false;
		this._session = new libSampleLocationCartSession(
			{
				Pict: () => this.pict,
				CartName: CART_NAME,
				HostID: CART_HOST_ID,
				OwnHash: () => `#/Sample/Workspace/${Number(this.sample.IDSample)}/location`,
				Toast: (pMessage, pType) => this.workspace._toast(pMessage, pType),
				OnMoved: (pMove) => this._onMoved(pMove),
				OnReceived: (pMark) => this._onReceived(pMark),
				OnLeave: () => { this.editing = false; },
			});
		/** @type {SampleLocationState} */
		this.state = SampleWorkspaceLocationTab.emptyState();
	}

	/** @return {SampleLocationState} */
	static emptyState()
	{
		return { Loaded: false, Loading: false, Error: '', Location: null, Ancestors: [], Join: null, AlsoIn: [] };
	}

	/** @return {any} */
	get pict()
	{
		return this.workspace.pict;
	}

	/** @return {Record<string, any>} the loaded sample */
	get sample()
	{
		return this.workspace.state.Sample || {};
	}

	/** @return {any} the cart's provider, once edit has attached it */
	get cart()
	{
		return this.pict.providers[CART_NAME];
	}

	/** A new sample is loading: forget the old one's location and close any cart. */
	reset()
	{
		this._closeCart();
		this.editing = false;
		this.state = SampleWorkspaceLocationTab.emptyState();
	}

	// --- data ----------------------------------------------------------------------------------------------------

	/**
	 * Read where the sample is: its location joins (newest first), that location, and the location tree for the
	 * locations it sits inside.
	 * @return {Promise<void>}
	 */
	async loadAsync()
	{
		const tmpIDSample = Number(this.sample.IDSample);
		if (!(tmpIDSample > 0))
		{
			return;
		}
		this.state.Loading = true;
		const tmpState = SampleWorkspaceLocationTab.emptyState();
		try
		{
			const tmpJoins = await new libSampleScanning.AppSampleScanStore(this.pict).getSampleLocations(tmpIDSample);
			if (!Array.isArray(tmpJoins))
			{
				throw new Error('could not read the sample\'s location');
			}
			const tmpLocationProvider = this.pict.providers.LocationProvider;
			const tmpLocations = (tmpJoins.length && tmpLocationProvider && (typeof tmpLocationProvider.getAllLocations === 'function')) ?
				await Promise.resolve(tmpLocationProvider.getAllLocations()).catch(() => []) : [];
			const fFind = async (pIDLocation) =>
			{
				const tmpFound = (tmpLocations || []).find((pLocation) => Number(pLocation.IDLocation) === Number(pIDLocation));
				if (tmpFound || !tmpLocationProvider || (typeof tmpLocationProvider.getLocationByID !== 'function'))
				{
					return tmpFound || null;
				}
				return Promise.resolve(tmpLocationProvider.getLocationByID(Number(pIDLocation))).catch(() => null);
			};
			if (tmpJoins.length)
			{
				tmpState.Join = tmpJoins[0];
				tmpState.Location = await fFind(tmpJoins[0].IDLocation);
				tmpState.Ancestors = libSampleScanning.LocationTree.getAncestors(tmpJoins[0].IDLocation, tmpLocations || []);
				for (const tmpJoin of tmpJoins.slice(1))
				{
					const tmpOther = await fFind(tmpJoin.IDLocation);
					if (tmpOther && !tmpState.AlsoIn.some((pLocation) => pLocation.IDLocation === tmpOther.IDLocation) && (tmpOther.IDLocation !== (tmpState.Location || {}).IDLocation))
					{
						tmpState.AlsoIn.push(tmpOther);
					}
				}
				if (!tmpState.Location)
				{
					tmpState.Location = { IDLocation: Number(tmpJoins[0].IDLocation) };
				}
			}
		}
		catch (pError)
		{
			this.pict.log.warn(`SampleWorkspace: could not read sample ${tmpIDSample}'s location: ${(pError && pError.message) || pError}`);
			tmpState.Error = 'The sample\'s location could not be read. Try again in a moment.';
		}
		tmpState.Loaded = true;
		this.state = tmpState;
	}

	/**
	 * Load once per sample, then redraw the tab if it is still showing.
	 * @return {Promise<void>}
	 */
	async ensureLoadedAsync()
	{
		if (this.state.Loaded || this.state.Loading)
		{
			return;
		}
		await this.loadAsync();
		if (this.workspace._activeTab === 'location')
		{
			this.workspace.repaintBody();
		}
	}

	// --- display ---------------------------------------------------------------------------------------------------

	/**
	 * @param {Record<string, any>} pLocation
	 * @return {string} the location's thumbnail, through GET Location/:IDLocation/Photo/Thumbnail; '' without a photo
	 */
	thumbnailHTML(pLocation)
	{
		if (!(Number(pLocation.IDObservation) > 0))
		{
			return '<div class="sw-loc-thumb sw-loc-nothumb" title="No photo"><i class="fas fa-location-dot"></i></div>';
		}
		const tmpPath = `Location/${Number(pLocation.IDLocation)}/Photo/Thumbnail`;
		let tmpURL = `/1.0/${tmpPath}`;
		const tmpRestClient = this.workspace.restClient;
		if (tmpRestClient && (typeof tmpRestClient.formatUrl === 'function'))
		{
			try
			{
				tmpURL = tmpRestClient.formatUrl(tmpPath);
			}
			catch (pError)
			{
				// keep the relative URL
			}
		}
		const tmpName = pLocation.Name || `Location ${pLocation.IDLocation}`;
		// A photo still being processed can't load yet: show the placeholder instead of a broken image.
		return `<img class="sw-loc-thumb" src="${this.workspace._esc(tmpURL)}" alt="Photo of ${this.workspace._esc(tmpName)}" loading="lazy" ` +
			'onerror="this.classList.add(\'is-hidden\'); this.nextElementSibling.classList.remove(\'is-hidden\');" />' +
			'<div class="sw-loc-thumb sw-loc-nothumb is-hidden" title="Photo not available yet"><i class="fas fa-location-dot"></i></div>';
	}

	/**
	 * @param {Record<string, any>} pLocation
	 * @return {string} a link to the location's workspace
	 */
	linkHTML(pLocation)
	{
		const tmpName = pLocation.Name || `Location ${pLocation.IDLocation}`;
		return `<a href="#/Location/Workspace/${Number(pLocation.IDLocation)}">${this.workspace._esc(tmpName)}</a>`;
	}

	/** @return {string} the Edit / Set location button, when the user may move the sample */
	editButtonHTML()
	{
		if (!this.workspace.canChangeSample())
		{
			return '';
		}
		const tmpLabel = this.state.Location ? 'Change location' : 'Set location';
		return `<button class="button is-small is-link" onclick="_Pict.providers.SampleWorkspace.editLocation()"><span class="icon"><i class="fas fa-pen"></i></span><span>${tmpLabel}</span></button>`;
	}

	/** @return {string} the display: where the sample is, or that it isn't anywhere */
	displayHTML()
	{
		const tmpEsc = (pValue) => this.workspace._esc(pValue);
		const tmpState = this.state;
		if (!tmpState.Loaded)
		{
			return '<div class="sw-state"><span class="pw-spinner pw-spinner-inline"></span> Loading the sample\'s location…</div>';
		}
		if (tmpState.Error)
		{
			return `<div class="sw-loc-error"><i class="fas fa-triangle-exclamation"></i> ${tmpEsc(tmpState.Error)}</div>`;
		}
		const tmpToolbar = `<div class="sw-toolbar">${this.editButtonHTML()}</div>`;
		const tmpLocation = tmpState.Location;
		if (!tmpLocation)
		{
			return `${tmpToolbar}${this.workspace._empty('This sample isn\'t in a location.')}`;
		}
		const tmpFacts = [];
		if (tmpLocation.Type)
		{
			tmpFacts.push(`<span class="sw-loc-type">${tmpEsc(tmpLocation.Type)}</span>`);
		}
		if (tmpLocation.QRCode)
		{
			tmpFacts.push(`<span class="sw-muted"><i class="fas fa-qrcode"></i> ${tmpEsc(tmpLocation.QRCode)}</span>`);
		}
		const tmpInside = tmpState.Ancestors.length ?
			`<div class="sw-loc-line">Inside ${tmpState.Ancestors.map((pLocation) => this.linkHTML(pLocation)).join(' <span class="sw-loc-sep">›</span> ')}</div>` : '';
		const tmpDescription = tmpLocation.Description ? `<div class="sw-loc-line">${tmpEsc(tmpLocation.Description)}</div>` : '';
		const tmpSince = (tmpState.Join && tmpState.Join.CreateDate) ? `<div class="sw-loc-line sw-muted">Here since ${tmpEsc(this.workspace._fmtDateTime(tmpState.Join.CreateDate))}</div>` : '';
		const tmpAlso = tmpState.AlsoIn.length ?
			`<div class="sw-loc-line sw-muted">Also listed in ${tmpState.AlsoIn.map((pLocation) => this.linkHTML(pLocation)).join(', ')}</div>` : '';
		return `${tmpToolbar}
			<div class="sw-loc-card">
				${this.thumbnailHTML(tmpLocation)}
				<div class="sw-loc-body">
					<div class="sw-loc-name">${this.linkHTML(tmpLocation)}</div>
					${tmpFacts.length ? `<div class="sw-loc-facts">${tmpFacts.join('')}</div>` : ''}
					${tmpInside}${tmpDescription}${tmpSince}${tmpAlso}
				</div>
			</div>`;
	}

	/** @return {string} edit mode: what to do, Cancel, and the host the cart draws into */
	editHTML()
	{
		return `<div class="sw-toolbar sw-loc-edit-bar">
				<span class="sw-muted">Choose or scan the sample's new location, then Move.</span>
				<button class="button is-small is-light" onclick="_Pict.providers.SampleWorkspace.cancelLocationEdit()"><span class="icon"><i class="fas fa-xmark"></i></span><span>Cancel</span></button>
			</div>
			<div id="${CART_HOST_ID}" class="sw-loc-cart"></div>`;
	}

	/** Draw the tab into the workspace body: the display, or the cart while editing. */
	render()
	{
		if (!this.editing)
		{
			this.pict.ContentAssignment.assignContent(BODY_SELECTOR, this.displayHTML());
			if (!this.state.Loaded && !this.state.Loading)
			{
				this.ensureLoadedAsync();
			}
			return;
		}
		this.pict.ContentAssignment.assignContent(BODY_SELECTOR, this.editHTML());
		this._mountCart();
	}

	// --- edit ------------------------------------------------------------------------------------------------------

	/** Swap the display for the cart, with this sample already in it. */
	edit()
	{
		if (!this.workspace.canChangeSample())
		{
			if (this.workspace.isLockedForUser())
			{
				this.workspace._toastLocked();
			}
			return;
		}
		this.editing = true;
		this.workspace.repaintBody();
	}

	/** Leave the cart without moving anything and show the display again. */
	cancel()
	{
		this._closeCart();
		this.editing = false;
		this.workspace.repaintBody();
	}

	/** Open the cart in the tab with this sample in it (it can't be removed from here), or redraw an open one. */
	_mountCart()
	{
		this._session.mount({ KeepSample: this.sample });
	}

	/** @return {boolean} whether the cart's camera is open */
	get cameraOpen()
	{
		return this._session.cameraOpen;
	}

	/**
	 * @param {string} pContainerID
	 * @return {Promise<boolean>} whether the camera is now open
	 */
	toggleCamera(pContainerID)
	{
		return this._session.toggleCamera(pContainerID);
	}

	_closeCart()
	{
		this._session.close();
	}

	/**
	 * Check Samples into Lab ran in the cart. When it set this sample to RECV, read the sample and its log again; the
	 * cart stays open, since marking samples received doesn't finish the move.
	 * @param {{ Received: Array<Record<string, any>> }} pMark
	 * @return {Promise<void>}
	 */
	async _onReceived(pMark)
	{
		const tmpIDSample = Number(this.sample.IDSample);
		if (!(pMark && Array.isArray(pMark.Received) && pMark.Received.some((pSample) => Number(pSample.IDSample) === tmpIDSample)))
		{
			return;
		}
		await this.workspace.refreshAfterMoveAsync();
	}

	/**
	 * The cart moved samples. Once this sample has moved, close the cart, read the sample again (a location can set
	 * its status) along with its log and location, and show the display.
	 * @param {{ Location: Record<string, any>, Moved: Array<Record<string, any>>, Failed: Array<Record<string, any>> }} pMove
	 * @return {Promise<void>}
	 */
	async _onMoved(pMove)
	{
		const tmpIDSample = Number(this.sample.IDSample);
		if (!this.editing || !(pMove && Array.isArray(pMove.Moved) && pMove.Moved.some((pSample) => Number(pSample.IDSample) === tmpIDSample)))
		{
			return;
		}
		this._closeCart();
		this.editing = false;
		this.state = SampleWorkspaceLocationTab.emptyState();
		await this.workspace.refreshAfterMoveAsync();
	}

}

module.exports = SampleWorkspaceLocationTab;
module.exports.CART_NAME = CART_NAME;
module.exports.CART_HOST_ID = CART_HOST_ID;
